import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Spinner, Box, Input } from '@amzn/awsui-components-react';
import renderMarkdown from './AIInsightMarkdown';
import useSmoothText from './useSmoothText';
import { useAIInsightChatContext } from './AIInsightChatContext';
import './AIInsight.scss';

interface AIInsightChatPanelProps {
    /**
     * Suggested prompts. Presentation config rather than conversation state, so it stays a
     * prop while the transcript and in-flight status come from AIInsightChatContext.
     */
    quickActions?: string[];
}

const DEFAULT_QUICK_ACTIONS = [
    'What is the VAR status?',
    'Show MAR failures',
    'LEF entity breakdown',
    'CWB workbook summary',
];

/** Distance from the bottom (px) within which the transcript keeps following new text. */
const FOLLOW_THRESHOLD_PX = 80;

/**
 * An assistant reply, revealed at a steady pace instead of in network-sized bursts. The full
 * text is always in state; only the display is paced, so a completed reply cannot be lost.
 *
 * `onRevealingChange` reports whether the display still trails the full text. The reveal can
 * outlast the network stream by up to the catch-up backlog, so the panel uses this — not just
 * `loading` — to decide when the live region is busy.
 */
function AssistantBubble({ text, onReveal, onRevealingChange }: {
    text: string;
    onReveal: () => void;
    onRevealingChange?: (revealing: boolean) => void;
}) {
    const shown = useSmoothText(text);
    const rendered = useMemo(() => renderMarkdown(shown), [shown]);
    const revealing = shown.length < text.length;

    useEffect(() => {
        onReveal();
    }, [shown, onReveal]);

    useEffect(() => {
        if (!onRevealingChange) {
            return undefined;
        }
        onRevealingChange(revealing);
        // Clear on unmount (or when the callback moves to a newer message), so a bubble that is
        // removed mid-reveal — e.g. the chat is reset — cannot leave the log stuck busy.
        return () => onRevealingChange(false);
    }, [revealing, onRevealingChange]);

    return <>{rendered}</>;
}

export default function AIInsightChatPanel({
    quickActions = DEFAULT_QUICK_ACTIONS,
}: AIInsightChatPanelProps) {
    const {
        messages,
        loading,
        error,
        pollingReportId,
        pollingElapsed,
        sendMessage,
    } = useAIInsightChatContext();
    const [input, setInput] = useState('');
    // True while the newest assistant reply is still being revealed (see AssistantBubble).
    const [revealing, setRevealing] = useState(false);
    const messagesRef = useRef<HTMLDivElement>(null);

    // While a reply is being revealed, keep the newest text in view — but only if the user is
    // already near the bottom, so scrolling up to reread is not yanked back down.
    const followReveal = useCallback(() => {
        const el = messagesRef.current;
        if (el && el.scrollHeight - el.scrollTop - el.clientHeight < FOLLOW_THRESHOLD_PX) {
            el.scrollTop = el.scrollHeight;
        }
    }, []);

    // A new message (the user's question, or the reply it starts) always brings the transcript
    // to the bottom. Keyed on the count, not the array: every streamed chunk replaces the array,
    // and scrolling on each one would override followReveal and pull a user who scrolled up to
    // reread back down. Instant rather than smooth, so followReveal's near-bottom check isn't
    // racing an in-flight scroll animation.
    useEffect(() => {
        const el = messagesRef.current;
        if (el) {
            el.scrollTop = el.scrollHeight;
        }
    }, [messages.length]);

    // Input and quick actions are blocked while a response streams or a report is pending,
    // so a second request cannot be queued against an in-flight agent session.
    const busy = loading || !!pollingReportId;

    const handleSend = () => {
        const question = input.trim();
        if (!question || busy) {
            return;
        }
        sendMessage(question);
        setInput('');
    };

    return (
        <div className="ai-insight-chat">
            <div
                ref={messagesRef}
                className="ai-insight-chat__messages"
                role="log"
                aria-live="polite"
                /* The reply is revealed a few characters at a time; without aria-busy a screen
                   reader would be fed a stream of tiny updates. Busy until the stream has ended
                   AND the reveal has caught up, so the reply is announced once, in full. */
                aria-busy={loading || revealing}
                aria-label="AI Insights conversation"
            >
                {messages.map((msg, i) => (
                    <div key={i} className={`ai-insight-chat__bubble-row ai-insight-chat__bubble-row--${msg.role}`}>
                        <div className={`ai-insight-chat__bubble ai-insight-chat__bubble--${msg.role}`}>
                            {msg.role === 'assistant'
                                ? (
                                    <AssistantBubble
                                        text={msg.text}
                                        onReveal={followReveal}
                                        // Only the newest reply can be mid-reveal; earlier ones
                                        // mount with their full text already shown.
                                        onRevealingChange={i === messages.length - 1 ? setRevealing : undefined}
                                    />
                                )
                                : msg.text}
                        </div>
                    </div>
                ))}
                {loading && (
                    <div className="ai-insight-chat__loading">
                        <Spinner size="normal" />
                        <span>Thinking...</span>
                    </div>
                )}
                {pollingReportId && !loading && (
                    <div className="ai-insight-chat__polling">
                        <Spinner size="normal" />
                        <div className="ai-insight-chat__polling-info">
                            <div className="ai-insight-chat__polling-title">Thinking...</div>
                            <div className="ai-insight-chat__polling-detail">
                                Report ID <code>{pollingReportId}</code>
                                {' · '}
                                {Math.floor(pollingElapsed / 1000)}s elapsed
                                {' · '}
                                {`I'll auto-validate once it's ready (up to 5 min).`}
                            </div>
                        </div>
                    </div>
                )}
                {error && (
                    <Box color="text-status-error" margin={{ bottom: 's' }}>
                        <span role="alert">{error}</span>
                    </Box>
                )}
            </div>

            <div className="ai-insight-chat__input-row">
                <div className="ai-insight-chat__input-field">
                    <Input
                        value={input}
                        ariaLabel="Ask AI Insights a question"
                        onChange={({ detail }) => setInput(detail.value)}
                        onKeyDown={({ detail }) => {
                            if (detail.key === 'Enter') handleSend();
                        }}
                        placeholder={pollingReportId
                            ? 'Waiting for report to be ready...'
                            : 'Ask about VAR, MAR, LEF, CWB reports...'}
                        disabled={busy}
                    />
                </div>
                <Button
                    onClick={handleSend}
                    loading={loading}
                    disabled={!input.trim() || busy}
                    iconName="send"
                    variant="primary"
                >
                    Send
                </Button>
            </div>

            <div className="ai-insight-chat__quick-actions">
                {quickActions.map(q => (
                    <Button
                        key={q}
                        variant="inline-link"
                        onClick={() => sendMessage(q)}
                        disabled={busy}
                    >
                        {q}
                    </Button>
                ))}
            </div>
        </div>
    );
}
